import { writeStorage } from '@rehooks/local-storage';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import maximizeMapShadow from '../assets/images/map/maximizeMapShadow.png';
import minimizeMapShadow from '../assets/images/map/minimizeMapShadowFull.png';
import minimizeMapShadowDisabled from '../assets/images/map/minimizeMapShadowFullDisabled.png';
import { FloatingMiniMap } from '../components/GameHUD';
import GuessingMap from '../components/GuessingMap';
import GuessingMapButton from '../components/GuessingMapButton';
import GuessLimitModal from '../components/GuessLimitModal';
import { routeNames } from '../constants/routes';
import { setLastResult } from '../redux/actions/result';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { resetLastPanoramaPlace } from '../redux/actions/pano';
import {
    MAX_DISTANCE_TO_GAIN_100_PERCENT_KM,
    MAX_SCORE_PERCENT,
    MIN_DISTANCE_FOR_POINTS_RANDOM,
    TOTAL_ROUNDS_MAX,
    guessResultMode,
} from '../constants/game';
import { MARKER_PLACE_ICON_KDETOSAKRA } from '../constants/icons';
import MapyCzContext from '../context/MapyCzContext';
import gameModes from '../enums/modes';
import useGetRandomUserToken from '../hooks/useGetRandomUserToken';
import useSMapResize from '../hooks/useSMapResize';
import { incrementMyTotalScore } from '../redux/actions/battle';
import { setTotalRoundCounter, resetStepCount } from '../redux/actions/game';
import { addGuessedRoundToPlayer, updateBattleRound } from '../services/firebase';
import { findUserFromBattleByRandomTokenId, getUnixTimestamp } from '../util';

export const GuessingMapContainer = ({
    isBattle,
    visible,
    evaluateGuessedRound,
    currentRoundGuessedPoint,
    panoramaLoading,
    findNewPanorama,
    saveRoundResult,
    panoramaScene,
    bestPanoramaPlace,
    allGuessedPoints,
    isGameStarted,
    currentCity,
    nextRoundButtonVisible,
    changeNextRoundButtonVisibility,
    mapDimension,
    onSetMapDimension,
}) => {
    const dispatch = useDispatch();
    const mapyContext = useContext(MapyCzContext);
    const randomUserToken = useGetRandomUserToken();
    const { width, height } = useSMapResize();
    const [showGuessLimitModal, setShowGuessLimitModal] = useState(false);
    const [guessButtonDisabled, setGuessButtonDisabled] = useState(true);
    const [currentRoundBattle, setCurrentRoundBattle] = useState();
    const [mapStyle, setMapStyle] = useState();
    const currentGame = useSelector(state => state.game.currentGame);
    const currentBattleInfo = useSelector(state => state.battle.currentBattle);
    // Aktuální pozice v panoramatu (pro režim hádání "end" - aktuální poloha)
    const lastPanoramaPlace = useSelector(state => state.pano);
    const [roundGuessed, setRoundGuessed] = useState(false);
    const [currentMapPointCoordinates, setCurrentMapPointCoordinates] = useState({
        mapLon: 0,
        mapLat: 0,
    });

    // common vars for both multiplayer and singleplayer
    const [mode, setMode] = useState();
    const [radius, setRadius] = useState();
    const [totalScore, setTotalScore] = useState();
    const [round, setRound] = useState();

    const refLayerValue = useRef();
    const refVectorLayerSMapValue = useRef();

    const { battleId, myDocumentId, myNickname, round: battleRound, rounds, players } = currentBattleInfo;

    useEffect(() => {
        if (width > 960) {
            if (!visible) {
                setMapStyle({ display: 'none' });
            }
            if (mapDimension === 'max') {
                setMapStyle({ height: height / 1.2, width: width / 1.5 });
            } else {
                setMapStyle({ height: height / 2, width: width / 3 });
            }
        } else {
            setMapStyle(null);
        }
    }, [width, height, mapDimension]);

    /*
    When the round is changed
     */
    useEffect(() => {
        if (round && rounds.length) {
            setCurrentRoundBattle(rounds[round - 1]);
            if (currentRoundGuessedPoint) {
                setRoundGuessed(true);
            } else {
                // when a new round is switched to
                // Staré SMap API - vyčistit vrstvy
                if (!FEATURE_FLAGS.USE_NEW_MAP_API) {
                    if (refLayerValue.current) {
                        refLayerValue.current.removeAll();
                    }
                    if (refVectorLayerSMapValue.current) {
                        refVectorLayerSMapValue.current.removeAll();
                    }
                }
                // Reset aktuální pozice panoramatu pro nové kolo (battle mód)
                dispatch(resetLastPanoramaPlace());
                dispatch(resetStepCount()); // Reset počítadla kroků při novém kole v battle módu
                setGuessButtonDisabled(true);
                changeNextRoundButtonVisibility(false);
                setRoundGuessed(false);
                window.scrollTo(0, 0);
            }
        }
    }, [round, rounds, currentRoundGuessedPoint]);

    useEffect(() => {
        const setCommonVars = (modeVar, radiusVar, totalScoreVar, roundVar) => {
            setMode(modeVar);
            setRadius(radiusVar);
            setTotalScore(totalScoreVar);
            setRound(roundVar);
        };

        if (isBattle && isGameStarted) {
            const { mode: battleMode, radius: battleRadius, myTotalScore } = currentBattleInfo;
            setCommonVars(battleMode, battleRadius, myTotalScore, battleRound);
        } else {
            const {
                mode: currentGameMode,
                radius: currentGameRadius,
                totalScore: currentGameTotalScore,
                round: currentGameRound,
            } = currentGame;
            setCommonVars(currentGameMode, currentGameRadius, currentGameTotalScore, currentGameRound);
        }
    }, [isBattle, isGameStarted, currentGame, currentBattleInfo, battleRound]);

    // Whether the guess button should be enabled (player hasn't guessed yet)
    const canPlayerGuess = useMemo(() => {
        if (isBattle) {
            if (!currentRoundBattle) {
                return false;
            }

            const { isRoundActive } = currentRoundBattle;
            if (!isRoundActive) {
                return false;
            }

            const myUser = findUserFromBattleByRandomTokenId(players, randomUserToken);
            return !myUser[`round${battleRound}`];
        }
        return true;
    }, [currentRoundBattle, battleRound, isBattle, players, randomUserToken]);

    // Whether the map should be visible at all (stays visible after guessing in battle mode)
    const guessingMapButtonVisible = useMemo(() => {
        if (isBattle) {
            if (!currentRoundBattle) {
                return false;
            }
            // Keep map visible as long as round exists (even after guessing)
            return true;
        }
        return true;
    }, [currentRoundBattle, isBattle]);

    // Check if player has already guessed this round
    const hasPlayerGuessedThisRound = useMemo(() => {
        if (!isBattle) return false;
        const myUser = findUserFromBattleByRandomTokenId(players, randomUserToken);
        return !!myUser?.[`round${battleRound}`];
    }, [isBattle, players, randomUserToken, battleRound]);

    const guessSingleplayerRound = () => {
        const guessedRoundPoint = calculateCoordsAndDrawGuess();
        evaluateGuessedRound(guessedRoundPoint);
    };

    const guessBattleRound = async () => {
        const guessedRoundPoint = calculateCoordsAndDrawGuess();
        const { isGuessed } = currentRoundBattle;

        // FIXME: check if I am not making a guess after time expiration

        evaluateGuessedRound(guessedRoundPoint);
        const { pointMap, pointPanorama, distance, score } = guessedRoundPoint;
        const playerRoundGuess = {
            [`round${battleRound}`]: {
                roundId: battleRound,
                pointPanorama: {
                    x: pointPanorama.x,
                    y: pointPanorama.y,
                },
                pointMap: {
                    x: pointMap.x,
                    y: pointMap.y,
                },
                distance,
                score,
            },
        };

        dispatch(incrementMyTotalScore(Math.round(score)));

        if (!isGuessed) {
            return addGuessedRoundToPlayer(battleId, myDocumentId, playerRoundGuess)
                .then(res => {
                    return updateBattleRound(battleId, battleRound, {
                        isGuessed: true,
                        guessedTime: getUnixTimestamp(new Date()),
                        firstGuess: {
                            guessedById: randomUserToken,
                            name: myNickname,
                        },
                    });
                })
                .then(res => {
                    console.info('Battle round updated.');
                })
                .catch(err => {
                    console.error('addGuessedRoundToPlayer and updateBattleRound: ', err);
                });
        }

        // spocti rozdil mezi guessedtime a timeoutem
        return addGuessedRoundToPlayer(battleId, myDocumentId, playerRoundGuess)
            .then(res => {})
            .catch(err => {
                console.error('addGuessedRoundToPlayer: ', err);
            });
    };

    const calculateDistance = () => {
        // Podpora starého i nového API formátu
        let panoramaCoordinates;
        let bestPanoLat, bestPanoLon;

        // Ověření, že máme data panoramatu
        if (!bestPanoramaPlace) {
            console.error('calculateDistance: bestPanoramaPlace is null');
            return { panoramaCoordinates: null, distance: 0, score: 0 };
        }

        if (FEATURE_FLAGS.USE_NEW_PANORAMA_API) {
            // Nové API - bestPanoramaPlace je přímo { lat, lon, date }
            if (!bestPanoramaPlace.lat || !bestPanoramaPlace.lon) {
                console.error('calculateDistance: bestPanoramaPlace has no lat/lon');
                return { panoramaCoordinates: null, distance: 0, score: 0 };
            }
            bestPanoLat = bestPanoramaPlace.lat;
            bestPanoLon = bestPanoramaPlace.lon;

            // Zjistíme, který režim hádání je aktivní
            const activeGuessResultMode = isBattle
                ? currentBattleInfo.guessResultMode
                : currentGame.guessResultMode;

            if (activeGuessResultMode === guessResultMode.end) {
                // Režim "end" - hádáme aktuální pozici v panoramatu
                // Použijeme lastPanoramaPlace (aktuální pozice), pokud existuje
                if (lastPanoramaPlace && lastPanoramaPlace.lat && lastPanoramaPlace.lon) {
                    panoramaCoordinates = {
                        lat: lastPanoramaPlace.lat,
                        lon: lastPanoramaPlace.lon,
                    };
                } else {
                    // Fallback na startovní pozici, pokud se hráč nepohnul
                    panoramaCoordinates = { lat: bestPanoLat, lon: bestPanoLon };
                }
            } else {
                // Režim "start" - hádáme startovní pozici
                panoramaCoordinates = { lat: bestPanoLat, lon: bestPanoLon };
            }
        } else {
            // Staré SMap API
            // eslint-disable-next-line no-underscore-dangle
            panoramaCoordinates = panoramaScene._place._data.mark;
            // eslint-disable-next-line no-underscore-dangle
            bestPanoLat = bestPanoramaPlace._data.mark.lat;
            // eslint-disable-next-line no-underscore-dangle
            bestPanoLon = bestPanoramaPlace._data.mark.lon;

            if (isBattle) {
                const { guessResultMode: guessResultModeBattle } = currentBattleInfo;
                if (guessResultModeBattle === guessResultMode.start) {
                    panoramaCoordinates = {
                        lat: bestPanoLat,
                        lon: bestPanoLon,
                    };
                }
            } else {
                const { guessResultMode: guessResultModeSinglePlayer } = currentGame;
                if (guessResultModeSinglePlayer === guessResultMode.start) {
                    panoramaCoordinates = {
                        lat: bestPanoLat,
                        lon: bestPanoLon,
                    };
                }
            }
        }

        let distance;
        if (
            panoramaCoordinates.lat === currentMapPointCoordinates.mapLat &&
            panoramaCoordinates.lon === currentMapPointCoordinates.mapLon
        ) {
            distance = 0;
        } else {
            const radlat1 = (Math.PI * panoramaCoordinates.lat) / 180;
            const radlat2 = (Math.PI * currentMapPointCoordinates.mapLat) / 180;
            const theta = panoramaCoordinates.lon - currentMapPointCoordinates.mapLon;
            const radtheta = (Math.PI * theta) / 180;
            let dist =
                Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
            if (dist > 1) {
                dist = 1;
            }
            dist = Math.acos(dist);
            dist = (dist * 180) / Math.PI;
            dist = dist * 60 * 1.1515;
            dist *= 1.609344; // convert from miles to kilometers
            distance = dist;
        }
        const score = calculateScore(distance);
        return {
            panoramaCoordinates,
            distance,
            score,
        };
    };

    const saveCurrentClickedMapPointCoordinates = coordinates => {
        setCurrentMapPointCoordinates(coordinates);
        setGuessButtonDisabled(false);
    };

    const calculateScore = distance => {
        let minDistanceForPoints;
        if (mode === gameModes.random || mode === gameModes.randomRegion) {
            minDistanceForPoints = MIN_DISTANCE_FOR_POINTS_RANDOM;
        } else {
            minDistanceForPoints = radius + 2;
        }
        let score;
        // distance less than 30 meters
        if (distance < MAX_DISTANCE_TO_GAIN_100_PERCENT_KM) {
            score = 100;
        } else {
            score = (minDistanceForPoints - distance) / (minDistanceForPoints / MAX_SCORE_PERCENT);
            if (score < 0) {
                score = 0;
            } else {
                score = score ** 2 / MAX_SCORE_PERCENT + distance / minDistanceForPoints;
                score = Math.max(0, score);
                score = Math.min(MAX_SCORE_PERCENT, score);
            }
        }

        saveRoundResult(score, distance);
        return score;
    };

    const drawGuessedDistance = (currentPanoramaPositionPoint, selectedPointOnMap, panoramaCoordinates) => {
        const markerPanoramaOptions = {
            url: MARKER_PLACE_ICON_KDETOSAKRA,
            anchor: { left: 10, bottom: 15 },
        };

        const guessedVectorPathCoordinates = [currentPanoramaPositionPoint, selectedPointOnMap];
        const vectorPathOptions = {
            color: '#f00',
            width: 3,
        };
        const path = new mapyContext.SMap.Geometry(
            mapyContext.SMap.GEOMETRY_POLYLINE,
            null,
            guessedVectorPathCoordinates,
            vectorPathOptions,
        );
        refVectorLayerSMapValue.current.addGeometry(path);
        // panorama place marker
        const markerPanorama = new mapyContext.SMap.Marker(
            mapyContext.SMap.Coords.fromWGS84(panoramaCoordinates.lon, panoramaCoordinates.lat),
            'Panorama point',
            markerPanoramaOptions,
        );
        refLayerValue.current.addMarker(markerPanorama);
    };

    const calculateCoordsAndDrawGuess = () => {
        setGuessButtonDisabled(true);
        changeNextRoundButtonVisibility(true);
        setRoundGuessed(true);

        const { panoramaCoordinates, distance, score } = calculateDistance();

        // Ošetření chybového stavu - panorama není k dispozici
        if (!panoramaCoordinates) {
            console.error('calculateCoordsAndDrawGuess: no panorama coordinates available');
            return {
                pointPanorama: { x: 0, y: 0 },
                pointMap: { x: currentMapPointCoordinates.mapLon, y: currentMapPointCoordinates.mapLat },
                currentCity,
                distance: 0,
                score: 0,
            };
        }

        // Pro nové API - jednoduchý formát souřadnic
        if (FEATURE_FLAGS.USE_NEW_MAP_API) {
            const guessedRoundPoint = {
                pointPanorama: {
                    x: panoramaCoordinates.lon,
                    y: panoramaCoordinates.lat,
                },
                pointMap: {
                    x: currentMapPointCoordinates.mapLon,
                    y: currentMapPointCoordinates.mapLat,
                },
                currentCity,
                distance,
                score,
            };
            return guessedRoundPoint;
        }

        // Staré SMap API
        const guessedPanoramaPositionPoint = mapyContext.SMap.Coords.fromWGS84(
            panoramaCoordinates.lon,
            panoramaCoordinates.lat,
        );
        const selectedPointOnMap = mapyContext.SMap.Coords.fromWGS84(
            currentMapPointCoordinates.mapLon,
            currentMapPointCoordinates.mapLat,
        );
        drawGuessedDistance(guessedPanoramaPositionPoint, selectedPointOnMap, panoramaCoordinates);
        const guessedRoundPoint = {
            pointPanorama: guessedPanoramaPositionPoint,
            pointMap: selectedPointOnMap,
            currentCity,
            distance,
            score,
        };
        return guessedRoundPoint;
    };

    /**
     * Refresh map for a new guessing!
     */
    const refreshMap = () => {
        findNewPanorama();
        // Staré SMap API - vyčistit vrstvy
        if (!FEATURE_FLAGS.USE_NEW_MAP_API) {
            refLayerValue.current?.removeAll();
            refVectorLayerSMapValue.current?.removeAll();
        }
        // Reset aktuální pozice panoramatu pro nové kolo
        dispatch(resetLastPanoramaPlace());
        changeNextRoundButtonVisibility(false);
        setGuessButtonDisabled(true);
        setRoundGuessed(false);
        window.scrollTo(0, 0);
    };

    const getSMapCollapseMin = () => {
        if (width > 960) {
            return { height: 100, width: 100 };
        }
        return null;
    };

    // Determine button text and state for FloatingMiniMap
    const isLastRound = (isBattle ? currentRoundBattle : currentGame.round) >= TOTAL_ROUNDS_MAX;
    const getButtonText = () => {
        if (isLastRound && roundGuessed) return 'Vyhodnotit hru';
        if (nextRoundButtonVisible && !isBattle) return 'Další kolo';
        if (guessButtonDisabled || panoramaLoading || !isGameStarted) return 'Klikni na mapu';
        return 'Hádat!';
    };

    const handleGuessClick = async () => {
        if (isBattle) {
            await guessBattleRound();
        } else {
            await guessSingleplayerRound();
        }
    };

    const handleNextRound = () => {
        if (isLastRound && roundGuessed) {
            // Redirect to results - handled by Link in the button
            dispatch(
                setLastResult({
                    guessedPoints: allGuessedPoints,
                    totalScore,
                    mode: currentGame?.mode,
                    city: currentGame?.city,
                    radius: currentGame?.radius,
                    isBattle,
                }),
            );
            window.location.href = `/${routeNames.vysledek}`;
        } else {
            refreshMap();
            if (round < TOTAL_ROUNDS_MAX) {
                dispatch(setTotalRoundCounter(round + 1));
                dispatch(resetStepCount()); // Reset počítadla kroků při novém kole
            }
        }
    };

    // Desktop: Use new FloatingMiniMap with hover-to-expand
    const isDesktop = width > 960 && !isMobile;

    return (
        <>
            {/* New GeoGuessr-style floating map for desktop */}
            {visible && isDesktop && guessingMapButtonVisible && (
                <FloatingMiniMap
                    onGuess={handleGuessClick}
                    onNextRound={handleNextRound}
                    disabled={guessButtonDisabled || panoramaLoading || !isGameStarted || !canPlayerGuess}
                    showNextRound={
                        isBattle
                            ? (isLastRound && roundGuessed) // V battle módu jen na posledním kole - přechody jsou automatické
                            : nextRoundButtonVisible || (isLastRound && roundGuessed)
                    }
                    buttonText={getButtonText()}
                    isLoading={panoramaLoading}
                    hasGuessed={roundGuessed || hasPlayerGuessedThisRound}
                    isBattle={isBattle}
                >
                    <GuessingMap
                        currentRoundGuessedPoint={currentRoundGuessedPoint}
                        isBattle={isBattle}
                        refLayerValue={refLayerValue}
                        roundGuessed={roundGuessed}
                        refVectorLayerSMapValue={refVectorLayerSMapValue}
                        saveCurrentClickedMapPointCoordinates={saveCurrentClickedMapPointCoordinates}
                    />
                </FloatingMiniMap>
            )}

            {/* Mobile/Tablet: Keep existing behavior */}
            {visible && !isDesktop && mapDimension !== 'min' && (
                <div
                    id="smap-container"
                    className="smap-container"
                    style={{
                        ...mapStyle,
                        ...(isMobile && {
                            height: height - 110,
                        }),
                    }}
                >
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
                    <img
                        alt="Maximální velikost mapy"
                        className={`smap-collapsible-max-max ${mapDimension === 'max' ? 'max-max-disabled' : ''}`}
                        src={mapDimension === 'max' ? minimizeMapShadowDisabled : minimizeMapShadow}
                        onClick={() => {
                            setMapStyle({ height: height / 1.2, width: width / 1.5 });
                            onSetMapDimension('max');
                            writeStorage('smapDimension', 'max');
                            window.dispatchEvent(new Event('resize'));
                        }}
                    />
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
                    <img
                        alt="Normální velikost mapy"
                        className="smap-collapsible-max"
                        src={minimizeMapShadow}
                        onClick={() => {
                            if (mapDimension === 'max') {
                                setMapStyle({ height: height / 2, width: width / 3 });
                                onSetMapDimension('normal');
                                writeStorage('smapDimension', 'normal');
                            } else if (mapDimension === 'normal') {
                                onSetMapDimension('min');
                                writeStorage('smapDimension', 'min');
                            }
                            window.dispatchEvent(new Event('resize'));
                        }}
                    />
                    <GuessingMap
                        currentRoundGuessedPoint={currentRoundGuessedPoint}
                        isBattle={isBattle}
                        refLayerValue={refLayerValue}
                        roundGuessed={roundGuessed}
                        refVectorLayerSMapValue={refVectorLayerSMapValue}
                        saveCurrentClickedMapPointCoordinates={saveCurrentClickedMapPointCoordinates}
                    />
                    {guessingMapButtonVisible && (
                        <GuessingMapButton
                            refreshMap={refreshMap}
                            isBattle={isBattle}
                            guessBattleRound={guessBattleRound}
                            guessSingleplayerRound={guessSingleplayerRound}
                            allGuessedPoints={allGuessedPoints}
                            round={round}
                            totalScore={totalScore}
                            roundGuessed={roundGuessed}
                            disabled={guessButtonDisabled || panoramaLoading || !isGameStarted}
                            currentRound={isBattle ? currentRoundBattle : currentGame.round}
                            currentGame={currentGame}
                            nextRoundButtonVisible={nextRoundButtonVisible}
                        />
                    )}
                </div>
            )}
            {!isDesktop && mapDimension === 'min' && (
                <div className="smap-container" style={getSMapCollapseMin()}>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
                    <img
                        alt="Zmenšená mapa"
                        className="smap-collapsible-min"
                        src={maximizeMapShadow}
                        onClick={() => {
                            onSetMapDimension('normal');
                            writeStorage('smapDimension', 'normal');
                            window.dispatchEvent(new Event('resize'));
                        }}
                    />
                </div>
            )}
            <GuessLimitModal
                visible={showGuessLimitModal}
                handleGuessModalVisibility={() => setShowGuessLimitModal(false)}
            />
        </>
    );
};
